import { Navigate, Outlet, useParams } from 'react-router-dom';
import { Sidebar } from '@/app/dashboard/Sidebar';
import { MobileNav } from '@/app/dashboard/MobileNav';
import { TopBar } from '@/app/dashboard/TopBar';
import { useAuth } from '@/lib/auth/useAuth';

/** The one guild ID that's allowed to render without a real session — an explicit, honestly-labeled preview, not a security bypass. */
const UNAUTHENTICATED_PREVIEW_GUILD_ID = 'preview-server';

/**
 * docs/ARCHITECTURE.md §1.3 — one of the three top-level layouts. Wraps
 * every /dashboard/:guildId/* route. Resolves guildId once (via useParams
 * inside Sidebar/TopBar/MobileNav) rather than leaf pages re-deriving it.
 *
 * Batch 5: real auth guard. This is UX, not the security boundary — the
 * Dem AI section (AiPage.tsx) now fetches/saves real guild-scoped config
 * through /api/guilds/:guildId/config/ai (see routes/guild-config.ts),
 * with every other section still local-preview state pending the same
 * conversion. Either way, this redirect is a convenience: every real
 * data-fetching call independently requires a valid session AND a live
 * Discord permission check server-side regardless of whether this guard
 * ever ran (docs/ARCHITECTURE.md §10).
 */
export function DashboardLayout() {
  const { status } = useAuth();
  const { guildId } = useParams<{ guildId: string }>();

  if (status === 'loading') {
    return <div className="h-full flex items-center justify-center text-text-tertiary text-small">Loading…</div>;
  }

  if (status === 'unauthenticated' && guildId !== UNAUTHENTICATED_PREVIEW_GUILD_ID) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="h-full flex">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar />
        <main className="flex-1 overflow-y-auto p-4 md:p-6 pb-20 md:pb-6">
          <Outlet />
        </main>
      </div>
      <MobileNav />
    </div>
  );
}
